import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    getStorage,
    ref,
    list,
    getDownloadURL,
    deleteObject,
    getMetadata,
} from 'firebase/storage';
import { useWorkspace } from '../../../context/WorkspaceContext';
import Breadcrumbs from '../../../components/Tools/Breadcrumbs';
import DropdownButton from '../../../components/Tools/DropdownButton';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { RiPencilLine, RiDeleteBinLine, RiDownload2Fill } from 'react-icons/ri';
import { FaEye, FaTimes, FaUser, FaGlobe } from 'react-icons/fa';
import { BiSolidVideoRecording } from 'react-icons/bi';
import { FaImage } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { MdFilterList } from 'react-icons/md';
import { Button } from '../../../ui/button';
import { LoadingIndicator } from '../../../components/Tools/LoadingIndicator';
import Progress from '../../../ui/progress';
import DisplayError from '../../../components/Tools/DisplayError';
import FilterPanel from './components/FilterPanel';
import StockLibrary from './StockLibrary';
import axios from 'axios';
import SpecialToggle from '../../../ui/specialToggle';
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const PAGE_SIZE = 20; // Number of files to load per page


const Library = ({ isPage = true, onConfirm, uploadType, isInModal = false, multiSelect = false }) => {
    // Main state for files and loading
    const [fileEntries, setFileEntries] = useState([]); // Lightweight entries initially
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [folderQueue, setFolderQueue] = useState([]);
    const [activeFolderIndex, setActiveFolderIndex] = useState(-1); // -1 for root, 0+ for folders

    const { activeWorkspace, idToken } = useWorkspace();
    const dialogRef = useRef(null);
    const filterPanelRef = useRef(null);
    const sentinelRef = useRef(null); // For infinite scroll
    const observerRef = useRef(null); // IntersectionObserver
    const gridRef = useRef(null);
    const detailObserverRef = useRef(null); // For lazy loading details

    // Use refs to avoid dependency cycles
    const loadingMoreRef = useRef(false);
    const hasMoreRef = useRef(true);
    const nextPageTokenRef = useRef(null);
    const activeFolderIndexRef = useRef(-1);
    const folderQueueRef = useRef([]);
    const currentFolderRef = useRef(null); // Track current folder to know when to reset pageToken

    const [selectedMedia, setSelectedMedia] = useState(null);
    const navigate = useNavigate();
    const [filesInUse, setFilesInUse] = useState({});

    // *** FILTER PANEL STATE ***
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [filterTitle, setFilterTitle] = useState('');
    const [filterMediaType, setFilterMediaType] = useState(uploadType === 'video' ? 'Videos' : uploadType === 'image' || uploadType === 'post' ? 'Images' : 'All Files');
    const [filterTimeUploaded, setFilterTimeUploaded] = useState('All Time');

    // *** SEGMENTED TOGGLE STATE ***
    const [activeLibraryView, setActiveLibraryView] = useState('my-library');
    
    // *** STOCK LIBRARY GROUPED VIEW STATE ***
    const [showStockGrouped, setShowStockGrouped] = useState(true);

    const resetFilters = () => {
        setFilterTitle('');
        setFilterMediaType(uploadType === 'video' ? 'Videos' : uploadType === 'image' || uploadType === 'post' ? 'Images' : 'All Files');
        setFilterTimeUploaded('All Time');
    };

    // Segmented toggle handler
    const handleLibraryViewToggle = (view) => {
        setActiveLibraryView(view);
        // You can add additional logic here like switching between personal and public media
    };

    // Stock Library grouped view toggle handler
    const handleStockGroupedToggle = () => {
        setShowStockGrouped(!showStockGrouped);
    };

    // NEW: State for selected files
    const [selectedFiles, setSelectedFiles] = useState([]);

    // Helper functions for file type checks
    const isVideo = (fileName) => /\.(mp4|mov|avi|wmv)$/i.test(fileName);
    const isImage = (fileName) => /\.(jpg|jpeg|png|gif|bmp|tiff)$/i.test(fileName);

    // Function to check if files are in use in Firestore
    const checkFilesInUse = useCallback(async () => {
        if (!activeWorkspace || !idToken || activeLibraryView !== 'my-library') {
            console.error('Cannot check files in use: missing workspace or token');
            return;
        }

        try {
            const response = await axios.get(`${BACKEND_API_URL}/api/quota/files-in-use`, {
                params: { workspaceId: activeWorkspace.id },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
            });

            const data = response.data;
            const inUseMap = {};

            if (data.filesInUse && Array.isArray(data.filesInUse) && data.filesData) {
                data.filesInUse.forEach(inUseFile => {
                    const fileData = data.filesData[inUseFile];
                    if (fileData && fileData.sources) {
                        // Extract the file path from the Firebase URL
                        let filePath = inUseFile;
                        try {
                            if (inUseFile.includes('firebasestorage.googleapis.com') ||
                                inUseFile.includes('googleapis.com/v0/b/') ||
                                inUseFile.includes('.appspot.com/o/')) {
                                const pathMatch = inUseFile.match(/\/o\/([^?]+)/);
                                if (pathMatch && pathMatch[1]) {
                                    filePath = decodeURIComponent(pathMatch[1]);
                                }
                            }
                        } catch (error) {
                            console.error("Error extracting path from URL:", error);
                        }

                        inUseMap[filePath] = {
                            inUse: true,
                            sources: fileData.sources
                        };
                    }
                });
            }
            setFilesInUse(inUseMap);
        } catch (error) {
            console.error('Error checking files in use:', error.response?.data || error.message);
        }
    }, [activeWorkspace, idToken, activeLibraryView]);

    // Fetch a page of files from Firebase Storage
    const fetchPage = useCallback(async () => {
        if (!activeWorkspace || !hasMoreRef.current || loadingMoreRef.current || activeLibraryView !== 'my-library') {
            return;
        }

        loadingMoreRef.current = true;
        setLoadingMore(true);

        try {
            const storage = getStorage();
            const workspaceRef = ref(storage, `${activeWorkspace.id}`);

            // Determine which folder to list
            let baseRef = workspaceRef;
            let currentPageToken = nextPageTokenRef.current;

            if (activeFolderIndexRef.current >= 0 && folderQueueRef.current[activeFolderIndexRef.current]) {
                baseRef = folderQueueRef.current[activeFolderIndexRef.current];
                // Only reset pageToken if we're moving to a different folder
                if (currentFolderRef.current !== baseRef.fullPath) {
                    currentPageToken = null;
                    currentFolderRef.current = baseRef.fullPath;
                }
            } else {
                currentFolderRef.current = 'root';
            }

            // List files with pagination
            const res = await list(baseRef, {
                maxResults: PAGE_SIZE,
                pageToken: currentPageToken || undefined,
            });

            // Convert StorageReference list to lightweight entries
            const newEntries = res.items.map(r => ({
                ref: r,
                fullPath: r.fullPath,
                name: r.name,
                _detailsLoaded: false,
                _loadingDetails: false,
            }));

            setFileEntries(prev => {
                const resetPrev = prev.map(f => f._loadingDetails && !f._detailsLoaded
                    ? { ...f, _loadingDetails: false }
                    : f
                );
                return [...resetPrev, ...newEntries];
            });

            // Handle pagination and folder navigation
            if (res.nextPageToken) {
                nextPageTokenRef.current = res.nextPageToken;
                setNextPageToken(res.nextPageToken);
                hasMoreRef.current = true;
                setHasMore(true);
            } else {
                // No more pages in current folder
                if (activeFolderIndexRef.current === -1) {
                    // We're in root, check if we have subfolders to process
                    if (res.prefixes && res.prefixes.length > 0) {
                        folderQueueRef.current = res.prefixes;
                        setFolderQueue(res.prefixes);
                        activeFolderIndexRef.current = 0;
                        setActiveFolderIndex(0);
                        nextPageTokenRef.current = null;
                        setNextPageToken(null);
                        hasMoreRef.current = true;
                        setHasMore(true);
                        // Continue to next folder immediately
                        setTimeout(() => fetchPage(), 0);
                    } else {
                        hasMoreRef.current = false;
                        setHasMore(false);
                    }
                } else {
                    // We're in a subfolder, move to next folder
                    const nextFolderIndex = activeFolderIndexRef.current + 1;
                    if (nextFolderIndex < folderQueueRef.current.length) {
                        activeFolderIndexRef.current = nextFolderIndex;
                        setActiveFolderIndex(nextFolderIndex);
                        nextPageTokenRef.current = null;
                        setNextPageToken(null);
                        hasMoreRef.current = true;
                        setHasMore(true);
                        // Continue to next folder immediately
                        setTimeout(() => fetchPage(), 0);
                    } else {
                        // No more folders
                        hasMoreRef.current = false;
                        setHasMore(false);
                    }
                }
            }

        } catch (error) {
            console.error('Error fetching page:', error);
            hasMoreRef.current = false;
            setHasMore(false);
        } finally {
            setLoading(false);
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [activeWorkspace, activeLibraryView]); // Only depend on activeWorkspace

    // Initial load
    useEffect(() => {
        if (activeWorkspace && activeLibraryView === 'my-library') {
            setFileEntries([]);
            setNextPageToken(null);
            setHasMore(true);
            setFolderQueue([]);
            setActiveFolderIndex(-1);
            setLoading(true);

            // Update refs
            nextPageTokenRef.current = null;
            hasMoreRef.current = true;
            folderQueueRef.current = [];
            activeFolderIndexRef.current = -1;
            currentFolderRef.current = null;

            fetchPage();
        }
    }, [activeWorkspace, fetchPage, activeLibraryView]);
    // Infinite scroll observer
    useEffect(() => {
        if (!sentinelRef.current || activeLibraryView !== 'my-library') return;

        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first.isIntersecting && hasMoreRef.current && !loadingMoreRef.current) {
                    setTimeout(() => fetchPage(), 100);
                }
            },
            { root: null, rootMargin: '400px', threshold: 0.01 }
        );

        observerRef.current.observe(sentinelRef.current);

        return () => observerRef.current && observerRef.current.disconnect();
    }, [fetchPage, activeLibraryView]);

    // Lazy loading details (URL + Metadata) for visible files
    useEffect(() => {
        if (!gridRef.current || activeLibraryView !== 'my-library') return;

        // Create observer only once
        if (!detailObserverRef.current) {
            detailObserverRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const fullPath = entry.target.getAttribute('data-fullpath');

                            setFileEntries(prev => {
                                const target = prev.find(f => f.fullPath === fullPath);
                                if (!target || target._detailsLoaded) {
                                    return prev;
                                }

                                // Mark as loading first
                                const updatedEntries = prev.map(f =>
                                    f.fullPath === fullPath
                                        ? { ...f, _loadingDetails: true }
                                        : f
                                );

                                // Load details asynchronously
                                (async () => {
                                    try {
                                        const [url, meta] = await Promise.all([
                                            getDownloadURL(target.ref),
                                            getMetadata(target.ref)
                                        ]);
                                        setFileEntries(current =>
                                            current.map(f => f.fullPath === fullPath
                                                ? {
                                                    ...f,
                                                    url,
                                                    size: meta.size,
                                                    timeCreated: meta.timeCreated,
                                                    updated: meta.updated,
                                                    contentType: meta.contentType,
                                                    md5Hash: meta.md5Hash,
                                                    _detailsLoaded: true,
                                                    _loadingDetails: false
                                                }
                                                : f
                                            )
                                        );
                                    } catch (error) {
                                        console.error('Lazy load error for', fullPath, error);
                                        setFileEntries(current =>
                                            current.map(f => f.fullPath === fullPath
                                                ? { ...f, _loadingDetails: false, _detailsLoaded: true, _error: true }
                                                : f
                                            )
                                        );
                                    }
                                })();

                                // Add timeout to reset stuck loading states
                                setTimeout(() => {
                                    setFileEntries(current => {
                                        const currentFile = current.find(f => f.fullPath === fullPath);
                                        if (currentFile && currentFile._loadingDetails && !currentFile._detailsLoaded) {
                                            return current.map(f => f.fullPath === fullPath
                                                ? { ...f, _loadingDetails: false }
                                                : f
                                            );
                                        }
                                        return current;
                                    });
                                }, 10000); // 10 second timeout

                                return updatedEntries;
                            });
                        }
                    });
                },
                { root: null, rootMargin: '200px', threshold: 0.1 }
            );
        }

        // Observe new tiles
        const tiles = gridRef.current.querySelectorAll('[data-fullpath]');
        tiles.forEach(t => {
            if (!t.hasAttribute('data-observed')) {
                detailObserverRef.current.observe(t);
                t.setAttribute('data-observed', 'true');
            }
        });

        return () => {
            if (detailObserverRef.current) {
                detailObserverRef.current.disconnect();
                detailObserverRef.current = null;
            }
        };
    }, [fileEntries.length, activeLibraryView]);

    // Check files in use once when workspace changes
    useEffect(() => {
        if (activeWorkspace && idToken && activeLibraryView === 'my-library') {
            checkFilesInUse();
        }
    }, [activeWorkspace, idToken, checkFilesInUse, activeLibraryView]);

    const handleCloseDialog = () => {
        if (dialogRef.current && dialogRef.current.open) {
            dialogRef.current.close();
        }
        setSelectedMedia(null);
        setShowFilterPanel(false);
    };

    // Update the click outside handler for dialog and filter panel.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dialogRef.current && dialogRef.current.open && !dialogRef.current.contains(event.target)) {
                setSelectedMedia(null);
                dialogRef.current.close();
            }
            
            // Check if click is on the filter button - if so, don't close the panel
            const filterButton = event.target.closest('button');
            const isFilterButton = filterButton && (
                filterButton.textContent.includes('Filter Items') || 
                filterButton.querySelector('svg') // The filter icon
            );
            
            if (showFilterPanel && filterPanelRef.current && 
                !filterPanelRef.current.contains(event.target) && 
                !isFilterButton) {
                setShowFilterPanel(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [showFilterPanel]);

    // Action handlers for individual file actions.
    const handleEditButtonClick = (file) => {
        if (activeLibraryView === 'my-library') {
            let uploadType = "unknown";
            if (isVideo(file.name)) {
                uploadType = "video";
            } else if (isImage(file.name)) {
                uploadType = "image";
            }
            const fileData = {
                posts: [
                    {
                        uploadType,
                        video_path: file.url,
                        firebase_path: file.url,
                    },
                ],
            };

            navigate(`/dashboard/upload`, { state: { upload: fileData } });
        }
    };

    const handleDeleteButtonClick = (fullPath) => {
        if (activeLibraryView === 'my-library') {
            const storage = getStorage();
            const fileReference = ref(storage, fullPath);
            deleteObject(fileReference)
                .then(() => {
                    setFileEntries((prevFiles) => prevFiles.filter((file) => file.fullPath !== fullPath));
                })
                .catch((error) => {
                    console.error('Error deleting file:', error);
                });
        }
    };

    const handleViewButtonClick = (file) => {
        if (activeLibraryView === 'my-library') {
            setSelectedMedia(file);
            if (dialogRef.current) {
                dialogRef.current.showModal();
            }
        }
    };

    const handleDownloadButtonClick = (file) => {
        if (activeLibraryView === 'my-library') {
            const functionUrl = `https://us-central1-socialshare-6946b.cloudfunctions.net/downloadFileFromLibrary`;
            const downloadUrl = `${functionUrl}?filePath=${encodeURIComponent(file.fullPath)}`;
            window.open(downloadUrl, '_blank');
        }
    };

    // Toggle the filter panel
    const toggleFilterPanel = () => {
        setShowFilterPanel(!showFilterPanel);
    };

    // Update the file selection toggle to handle both single and multi-select modes
    const toggleFileSelection = (file) => {
        if (activeLibraryView === 'my-library') {
            if (!isPage && !multiSelect) {
                // Single selection mode for modal (default behavior)
                setSelectedFiles([file]);
            } else if (!isPage && multiSelect) {
                // Multi-select mode for modal
                setSelectedFiles((prev) => {
                    const alreadySelected = prev.some((f) => f.fullPath === file.fullPath);
                    if (alreadySelected) {
                        return prev.filter((f) => f.fullPath !== file.fullPath);
                    } else {
                        return [...prev, file];
                    }
                });
            } else {
                // Page mode (always multi-select)
                setSelectedFiles((prev) => {
                    const alreadySelected = prev.some((f) => f.fullPath === file.fullPath);
                    if (alreadySelected) {
                        return prev.filter((f) => f.fullPath !== file.fullPath);
                    } else {
                        return [...prev, file];
                    }
                });
            }
        }
    };

    // NEW: Multi-select actions.
    const handleDownloadSelected = () => {
        if (activeLibraryView === 'my-library') {
            selectedFiles.forEach((file) => {
                handleDownloadButtonClick(file);
            });
        }
    };

    const handleDeleteSelected = () => {
        if (activeLibraryView === 'my-library') {
            if (!window.confirm("Are you sure you want to delete the selected files?")) {
                return;
            }
            selectedFiles.forEach((file) => {
                handleDeleteButtonClick(file.fullPath);
            });
            // Clear the selection after deletion.
            setSelectedFiles([]);
        }
    };

    const cancelMultiSelect = () => {
        if (activeLibraryView === 'my-library') {
            setSelectedFiles([]);
        }
    };

    // Add a confirm button handler
    const handleConfirm = () => {
        if (activeLibraryView === 'my-library' && selectedFiles.length > 0) {
            if (multiSelect) {
                // Pass array of selected files for multiselect
                onConfirm(selectedFiles);
            } else {
                // Pass single file for single select (backward compatibility)
                onConfirm(selectedFiles[0]);
            }
        }
    };

    // Compute filtered files.
    const filteredFiles = fileEntries.filter((file) => {
        if (filterTitle && !file.name.toLowerCase().includes(filterTitle.toLowerCase())) {
            return false;
        }
        if (filterMediaType === 'Images' && !isImage(file.name)) {
            return false;
        }
        if (filterMediaType === 'Videos' && !isVideo(file.name)) {
            return false;
        }
        // Apply additional filter based on passed uploadType
        if (uploadType === 'video' && !isVideo(file.name)) {
            return false;
        }
        if ((uploadType === 'image' || uploadType === 'post') && !isImage(file.name)) {
            return false;
        }

        // Filter by time uploaded (only if details are loaded)
        if (filterTimeUploaded !== 'All Time' && file.timeCreated) {
            const fileDate = new Date(file.timeCreated);
            const now = new Date();

            if (filterTimeUploaded === 'Last 24 hours') {
                const yesterday = new Date(now);
                yesterday.setDate(now.getDate() - 1);
                if (fileDate < yesterday) {
                    return false;
                }
            } else if (filterTimeUploaded === 'Last 7 days') {
                const lastWeek = new Date(now);
                lastWeek.setDate(now.getDate() - 7);
                if (fileDate < lastWeek) {
                    return false;
                }
            } else if (filterTimeUploaded === 'Last 30 days') {
                const lastMonth = new Date(now);
                lastMonth.setDate(now.getDate() - 30);
                if (fileDate < lastMonth) {
                    return false;
                }
            }
        }

        return true;
    });
    // No sorting - keep files in the order they were loaded for better UX

    // State for storage usage data
    const [storageUsage, setStorageUsage] = useState({
        total: 0,
        limit: 500,
        images: 0,
        videos: 0,
    });

    // Fetch storage usage data from backend
    useEffect(() => {
        const fetchStorageUsage = async () => {
            if (!activeWorkspace || !idToken || activeLibraryView !== 'my-library') return;

            try {
                const response = await axios.get(`${BACKEND_API_URL}/api/quota/storage`, {
                    headers: { Authorization: `Bearer ${idToken}` },
                    params: { workspaceId: activeWorkspace.id }
                });

                if (response.data) {
                    const { imagesSize, videosSize, totalSize, storageLimit } = response.data;

                    // Convert bytes to MB
                    const mbBytes = 1024 * 1024;
                    const imageMB = (imagesSize / mbBytes).toFixed(2);
                    const videoMB = (videosSize / mbBytes).toFixed(2);
                    const totalMB = (totalSize / mbBytes).toFixed(2);

                    setStorageUsage({
                        total: parseFloat(totalMB),
                        limit: storageLimit,
                        images: parseFloat(imageMB),
                        videos: parseFloat(videoMB),
                    });
                }
            } catch (error) {
                console.error('Error fetching storage usage:', error);
            }
        };

        fetchStorageUsage();
    }, [activeWorkspace, idToken, activeLibraryView]);

    const handleRemoveUnused = async () => {
        if (activeLibraryView === 'my-library') {
            // Filter out files that are in use (only from loaded files)
            const loadedFiles = fileEntries.filter(f => f._detailsLoaded && f.url);
            const unusedFiles = loadedFiles.filter(file => !filesInUse[file.fullPath] || !filesInUse[file.fullPath].inUse);

            if (unusedFiles.length === 0) {
                alert("No unused files found to remove.");
                return;
            }

            if (!window.confirm(`Found ${unusedFiles.length} unused files. Do you want to delete them?`)) {
                return;
            }

            const storage = getStorage();

            for (const file of unusedFiles) {
                const fileRef = ref(storage, file.fullPath);
                try {
                    await deleteObject(fileRef);
                } catch (error) {
                    console.error('Error deleting file:', error);
                }
            }

            setFileEntries(prevFiles => prevFiles.filter(f =>
                filesInUse[f.fullPath] && filesInUse[f.fullPath].inUse
            ));
        }
    };



    return (
        <div className="dashboard-container flex flex-col w-full px-2 sm:px-4 relative">
            <div className="flex items-center justify-between pt-3 mb-2">
                {isPage && (

                    <Breadcrumbs />
                )}

                <SpecialToggle
                    activeOption={activeLibraryView}
                    onToggle={handleLibraryViewToggle}
                    options={[
                        {
                            value: 'my-library',
                            label: 'My Library',
                            icon: <FaUser size={16} />
                        },
                        {
                            value: 'public-media',
                            label: 'Public Media',
                            icon: <FaGlobe size={16} />
                        }
                    ]}
                />
            </div>

            {isPage && activeLibraryView === 'my-library' && <StorageUsage storageUsage={storageUsage} />}
            {/* Dashboard section header */}
            <div className="w-full mb-2 sm:mb-4 dashboard-section">
                <div className="flex flex-wrap sm:grid sm:grid-cols-3 items-center gap-2 sm:gap-0">
                    {/* Left section */}
                    <div className="flex justify-start gap-1 sm:gap-2 order-1 sm:order-1">
                        <Button size="sm" variant="outline" onClick={toggleFilterPanel} className="p-1 sm:p-2">
                            <MdFilterList />
                            <span className="hidden sm:inline hover:text-black">Filter Items</span>
                        </Button>
                        {isPage && activeLibraryView === 'my-library' && <Button size="sm" variant="outline" onClick={handleRemoveUnused} className="p-1 sm:p-2">
                            <span className="hidden sm:inline hover:text-black">Remove Unused</span>
                        </Button>}
                    </div>

                                {/* Center section */}
            <div className="flex justify-center w-full sm:w-auto order-0 sm:order-2 mb-2 sm:mb-0">
                <h1 className="text-xl sm:text-2xl font-bold">
                    {activeLibraryView === 'my-library' ? 'Library' : 'Stock Library'}
                </h1>
            </div>

                    {/* Right section */}
                    <div className="flex justify-end space-x-1 sm:space-x-2 order-2 sm:order-3">
                        {/* Stock Library Grouped View Toggle */}
                        {isPage && activeLibraryView === 'public-media' && (
                            <Button 
                                onClick={handleStockGroupedToggle}
                                variant={showStockGrouped ? "outline" : "primary"}
                                size="sm"
                                className="p-1 sm:p-2"
                            >
                                    {showStockGrouped ? "All Media" : "Grouped Topics"}
                            </Button>
                        )}
                        
                        {/* My Library Multi-select Actions */}
                        {isPage && activeLibraryView === 'my-library' && selectedFiles.length > 0 && (
                            <>
                                <Button size="sm" variant="outline" onClick={handleDownloadSelected} className="p-1 sm:p-2">
                                    <RiDownload2Fill />
                                    <span className="hidden sm:inline">Download Selected</span>
                                </Button>
                                <Button size="sm" variant="destructive" onClick={handleDeleteSelected} className="p-1 sm:p-2">
                                    <RiDeleteBinLine />
                                    <span className="hidden sm:inline !text-white">Delete Selected</span>
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelMultiSelect} className="p-1 sm:p-2">
                                    <FaTimes />
                                    <span className="hidden sm:inline">Cancel</span>
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Filter Panel - positioned as overlay above content */}
            {showFilterPanel && (
                <div ref={filterPanelRef} className="absolute top-[140px] left-4 z-30">
                    <FilterPanel
                        filterTitle={filterTitle}
                        setFilterTitle={setFilterTitle}
                        filterMediaType={filterMediaType}
                        setFilterMediaType={setFilterMediaType}
                        filterTimeUploaded={filterTimeUploaded}
                        setFilterTimeUploaded={setFilterTimeUploaded}
                        resetFilters={resetFilters}
                        applyFilters={() => {}}
                        isInModal={!isPage}
                    />
                </div>
            )}

            {/* Conditional rendering based on library view */}
            {activeLibraryView === 'my-library' ? (
                <>

                    {/* Files Grid or Loading Indicator */}
                    {loading ? (
                        <div className="flex justify-center items-center h-screen">
                            <LoadingIndicator showLogo={true} />
                        </div>
                    ) : fileEntries.length === 0 ? (
                        <div className="flex justify-center items-center h-full max-w-[100%] w-[300px] mx-auto">
                            <DisplayError />
                        </div>
                    ) : (
                        <div
                            ref={gridRef}
                            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 py-2"
                        >
                            {filteredFiles.map((file, index) => {
                                // Determine if this file is selected in multi-select mode.
                                const isSelected = selectedFiles.some((f) => f.fullPath === file.fullPath);
                                const fileUsage = filesInUse[file.fullPath];
                                const loaded = file._detailsLoaded && file.url && !file._error;
                                const isVid = isVideo(file.name);

                                return (
                                    <div
                                        key={`${file.fullPath}-${index}`}
                                        data-fullpath={file.fullPath}
                                        onClick={() => toggleFileSelection(file)}
                                        className={`cursor-pointer relative bg-gray-100 rounded-lg overflow-hidden aspect-square shadow hover:shadow-lg transition-shadow duration-200 flex flex-col justify-between ${isSelected ? 'border-2 sm:border-4 border-blue-500' : ''}`}
                                    >
                                        <div className="flex justify-between items-start">
                                            {!loaded && !file._error && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="text-xs text-gray-500 animate-pulse">Loading...</div>
                                                </div>
                                            )}
                                            {file._error && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="text-xs text-red-500">Error</div>
                                                </div>
                                            )}
                                            {loaded && (
                                                isVid ? (
                                                    <video
                                                        className="object-cover w-full h-full"
                                                        src={file.url}
                                                        muted
                                                        loop
                                                        playsInline
                                                        preload="metadata"
                                                    />
                                                ) : (
                                                    <img
                                                        src={file.url}
                                                        alt={file.name}
                                                        className="object-cover w-full h-full"
                                                        loading="lazy"
                                                    />
                                                )
                                            )}

                                            {/* Media Type Icon */}
                                            <div className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-black bg-opacity-50 rounded-full p-1 sm:p-1.5">
                                                {isVid ? (
                                                    <BiSolidVideoRecording className="text-white" size={16} />
                                                ) : (
                                                    <FaImage className="text-white" size={16} />
                                                )}
                                            </div>

                                            {/* Only show the dropdown if not in multi-select mode and isPage is true */}
                                            {!selectedFiles.length && isPage && (
                                                <DropdownButton
                                                    label={<BsThreeDotsVertical size={18} color="black" />}
                                                    classes="absolute top-1 sm:top-2 right-1 sm:right-2 z-10"
                                                    options={[
                                                        {
                                                            label: 'View',
                                                            onClick: () => handleViewButtonClick(file),
                                                            icon: <FaEye size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />,
                                                        },
                                                        {
                                                            label: 'Create post',
                                                            onClick: () => handleEditButtonClick(file),
                                                            icon: (
                                                                <RiPencilLine size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />
                                                            ),
                                                        },
                                                        {
                                                            label: 'Download',
                                                            onClick: () => handleDownloadButtonClick(file),
                                                            icon: (
                                                                <RiDownload2Fill size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />
                                                            ),
                                                        },
                                                        {
                                                            label: 'Delete',
                                                            onClick: () => handleDeleteButtonClick(file.fullPath),
                                                            icon: (
                                                                <RiDeleteBinLine size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />
                                                            ),
                                                        },
                                                    ]}
                                                />
                                            )}

                                            {/* In Use Badge with Sources */}
                                            {fileUsage && fileUsage.inUse && (
                                                <div className='flex flex gap-1 absolute top-1 sm:top-2 left-10 sm:left-12 z-10'>
                                                    <div className="bg-purple-500 text-white text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded-md">
                                                        In Use
                                                    </div>
                                                    {fileUsage.sources && fileUsage.sources.map((source, idx) => (
                                                        <div
                                                            key={idx}
                                                            className={`text-white text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded-md ${source === 'posts' ? 'bg-blue-500' :
                                                                    source === 'drafts' ? 'bg-yellow-500' :
                                                                        source === 'linkinbio' ? 'bg-green-500' :
                                                                            source === 'workspace' ? 'bg-red-500' :
                                                                                'bg-gray-500'
                                                                }`}
                                                        >
                                                            {source === 'posts' ? 'Post' :
                                                                source === 'drafts' ? 'Draft' :
                                                                    source === 'linkinbio' ? 'Bio' :
                                                                        source === 'workspace' ? 'Workspace' :
                                                                            source}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Sentinel for infinite scroll */}
                    {hasMore && (
                        <div ref={sentinelRef} className="py-6 flex justify-center">
                            {loadingMore && <span className="text-sm text-gray-500">Loading more...</span>}
                        </div>
                    )}
                    {!hasMore && !loading && fileEntries.length > 0 && (
                        <div className="py-4 text-center text-xs text-gray-400">No more files.</div>
                    )}
                </>
            ) : (
                /* Stock Library View */
                <StockLibrary 
                    isPage={isPage}
                    onConfirm={onConfirm}
                    uploadType={uploadType}
                    isInModal={isInModal}
                    multiSelect={multiSelect}
                    filterTitle={filterTitle}
                    filterMediaType={filterMediaType}
                    showGrouped={showStockGrouped}
                    onToggleGrouped={handleStockGroupedToggle}
                />
            )}

            {/* View Media Dialog - Only for personal library */}
            {activeLibraryView === 'my-library' && (
                <dialog
                    ref={dialogRef}
                    className="w-full max-w-2xl p-0 flex items-center justify-center mx-auto my-auto"
                    onClick={isInModal ? null : (e) => e.stopPropagation()}
                >
                    {selectedMedia && (
                        <div className="relative bg-white p-5 rounded-lg">
                            <div onClick={handleCloseDialog} className="absolute top-2 right-2 text-black">
                                <FaTimes color="black" size={20} />
                            </div>
                            {isVideo(selectedMedia.name) ? (
                                <video src={selectedMedia.url} controls className="max-w-full max-h-[80vh]" />
                            ) : (
                                <img
                                    src={selectedMedia.url}
                                    alt={selectedMedia.name}
                                    className="max-w-full max-h-[80vh]"
                                />
                            )}
                        </div>
                    )}
                </dialog>
            )}

            {/* Confirm Button at Bottom Right */}
            {!isPage && selectedFiles.length > 0 && activeLibraryView === 'my-library' && (multiSelect || selectedFiles.length === 1) && (
                <div className="fixed bottom-4 right-4">
                    <Button size="xl" variant="default" onClick={handleConfirm}>
                    Confirm {multiSelect && selectedFiles.length > 1 ? `(${selectedFiles.length})` : ''}
                    </Button>
                </div>
            )}
        </div>
    );
};


const StorageUsage = ({ storageUsage }) => {
    const [opacity, setOpacity] = useState(1);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const maxScroll = 200; // Adjust this value as needed
            const newOpacity = Math.max(0.5, 1 - scrollTop / maxScroll);
            setOpacity(newOpacity);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div
            className="dashboard-section w-full md:w-[500px] fixed bottom-14 md:bottom-5 md:right-5 right-0 z-10"
            style={{ opacity, pointerEvents: 'none' }}
        >
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Storage Usage</h3>
                <span className="text-sm text-gray-500">
                    {storageUsage.total}MB of {storageUsage.limit}MB used
                </span>
            </div>
            <Progress value={(storageUsage.total / storageUsage.limit) * 100} />
            <div className="mt-2 flex gap-4 text-sm text-gray-500">
                <span>Images: {storageUsage.images}MB</span>
                <span>Videos: {storageUsage.videos}MB</span>
            </div>
        </div>
    );
};
export default Library;